export function newBasketId() {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function parsePrice(value) {
  const match = String(value || '').match(/^(\d+)(?:\.(\d{1,2}))? ([A-Z]{3})$/);
  if (!match) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? { cents, currency: match[3] } : null;
}

export const priceString = (cents, currency) => `${(cents / 100).toFixed(2)} ${currency}`;

export function lineDetails(line, products) {
  const product = products.find(item => item.id === line.product_id);
  const sku = product?.variants?.find(item => item.id === line.sku_id);
  const base = parsePrice(sku?.price);
  const fail = message => ({ valid: false, message, product, sku, name: product?.name || 'Unavailable item', quantity: line.quantity });
  if (!product || !sku || !sku.orderable || !base) return fail('This item has changed or must be ordered with our team.');
  if (!Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > 20) return fail('Choose a quantity from 1 to 20.');
  const groups = (product.option_groups || []).filter(group => sku.option_group_ids?.includes(group.id));
  const counts = {}, seen = new Set(), choices = [];
  let cents = base.cents;
  for (const selection of line.options || []) {
    const group = groups.find(item => item.id === selection.group_id);
    const option = group?.options.find(item => item.id === selection.option_id);
    const amount = parsePrice(option?.price);
    const pair = `${selection.group_id}:${selection.option_id}`;
    const count = selection.quantity || 1;
    if (!option?.orderable || !amount || amount.currency !== base.currency || seen.has(pair)
      || !Number.isInteger(count) || count < 1 || count > 20 || (count > 1 && !group.multiple_selection)) return fail('Please review the choices for this item.');
    seen.add(pair);
    counts[group.id] = (counts[group.id] || 0) + count;
    cents += amount.cents * count;
    choices.push(`${count > 1 ? `${count}× ` : ''}${option.name}`);
  }
  for (const group of groups) {
    if ((counts[group.id] || 0) < (group.min_selections || 0)
      || (group.max_selections != null && (counts[group.id] || 0) > group.max_selections)) return fail(`Please review the choices for ${group.name}.`);
  }
  return { valid: true, product, sku, name: product.name, choices, quantity: line.quantity, cents, currency: base.currency, total: cents * line.quantity };
}

export function basketDetails(lines, products) {
  const details = lines.map(line => lineDetails(line, products));
  const currency = details.find(item => item.valid)?.currency;
  const valid = details.length > 0 && details.every(item => item.valid && item.currency === currency)
    && lines.length <= 50 && lines.reduce((count, line) => count + line.quantity, 0) <= 100;
  const cents = details.reduce((total, item) => total + (item.valid ? item.total : 0), 0);
  return { valid, details, cents, currency, total: currency ? priceString(cents, currency) : null };
}

export const emptyBasket = token => ({ token, lines: [], pending: null, pendingSnapshot: null, receipt: null, history: [], guestName: '', guestEmail: '', notes: '', updatedAt: Date.now() });
const storageKey = token => `bplatform:guest-basket:${token}`;

export function loadBasket(token) {
  const fallback = emptyBasket(token);
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey(token)));
    if (!stored || stored.token !== token || !Array.isArray(stored.lines) || stored.lines.length > 50) return fallback;

    if (stored.lines.some(line => !line || typeof line.id !== 'string' || typeof line.product_id !== 'string' || typeof line.sku_id !== 'string'
      || !Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > 20 || !Array.isArray(line.options)
      || line.options.some(option => !option || typeof option.group_id !== 'string' || typeof option.option_id !== 'string'))) return fallback;
    if (typeof stored.guestName !== 'string' || typeof stored.notes !== 'string') return fallback;
    const history=Array.isArray(stored.history) ? stored.history.filter(order => order && typeof order.reference === 'string' && typeof order.total === 'string' && Array.isArray(order.items)).slice(0, 50) : [];
    if (!stored.pending && Date.now() - stored.updatedAt > 24 * 60 * 60 * 1000) return {...fallback,history};
    return { ...fallback, ...stored, history };
  } catch { return fallback; }
}

export function storeBasket(basket) {
  try { localStorage.setItem(storageKey(basket.token), JSON.stringify({ ...basket, updatedAt: Date.now() })); return true; }
  catch { return false; }
}

export function lineFingerprint(line) {
  return JSON.stringify([line.product_id, line.sku_id, (line.options || []).map(option => [option.group_id, option.option_id, option.quantity || 1]).sort(), (line.notes || '').trim()]);
}
